/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ValidationErrors } from '@angular/forms';
import {
  ContactCustomerType, EmailCampaignAccountInfoDto, EmailCampaignAccountType,
  EmailCampaignDetailsDto,
  EmailCampaignFilterDto,
  EmailCampaignListItemDto,
  EmailCampaignRecipientDto,
  EmailCampaignRecipientFilterDto,
  EmailCampaignReportDto,
  EmailCampaignStatus,
  Priority,
  SalesFolderStatus,
  Stage,
} from '@gh/builder-shared-data';
import {
  EntityConfig,
  entityValidatorByEntityConfig,
  formStateSnapshot,
  FormStateSnapshot,
  getFormStateSnapshotValue,
} from '@gh/common-shared';
import {
  AutocompleteItem,
  AutocompleteItemDto,
  EMPTY_ID,
  ID,
  WithVersion,
} from '@gh/common-shared-data';
import { validationError } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import {
  cast,
  cloneDate,
  currentDate,
  DateTime,
  dateTimeFromDate,
  dateTimeToDate,
  isDateInvalid,
  isDateTimeValid,
  truncateDateToMinutes,
} from '@gh/core-util';
import { isEmpty, isNil, map, omit } from 'lodash';
import { Enum } from '@gh/core-data';

export type EmailCampaignListItem = EmailCampaignListItemDto;
export type EmailCampaignFilter = EmailCampaignFilterDto;
export type EmailCampaignReport = EmailCampaignReportDto;

export const getEmailCampaignRecipientId = (recipient: EmailCampaignRecipient) => recipient.contactId;
export const isEmailCampaignRecipientHasBlankEmail =
  (recipient: EmailCampaignRecipient) => isEmpty(recipient.contactEmail);

export const EMAIL_CAMPAIGN_DEFAULT_FILTER: EmailCampaignFilter = {
  showPerformed: true,
  showCancelled: false,
};

export type EmailCampaignMasterInfo = {
  name: string;
  externalTemplate: AutocompleteItem;
  performedDate: DateTime;
};

export type AllOption = '_All';
export const ALL_OPTION: AllOption = '_All';
export type WithAllOption<T> = T | AllOption;

export function withoutAllOption<T>(option: WithAllOption<T>): Maybe<T> {
  return option === ALL_OPTION ? void 0 : option;
}

export type EmailCampaignRecipient = EmailCampaignRecipientDto;
export type EmailCampaignAccountInfo = EmailCampaignAccountInfoDto;
export type EmailCampaignRecipientFilter = {
  offices: AutocompleteItemDto[];
  masterZones: AutocompleteItemDto[];
  salespersons: AutocompleteItemDto[];
  communities: AutocompleteItemDto[];
  priorities: Priority[];
  salesFolderStages: Stage[];
  typeOfCustomer: WithAllOption<ContactCustomerType>;
  salesFolderStatus: WithAllOption<SalesFolderStatus>;
};

export type EmailCampaignDetails = WithVersion<{
  campaignId: ID;
  status: EmailCampaignStatus;
  externalId: ID;
  externalListId: ID;
  masterInfo: FormStateSnapshot<EmailCampaignMasterInfo>;
  appliedFilter: FormStateSnapshot<EmailCampaignRecipientFilter>;
  currentFilter: FormStateSnapshot<EmailCampaignRecipientFilter>;
  recipients: EmailCampaignRecipient[];
  recipientsLoading: boolean;
  paragraph: string;
  focusedCell?: FocusedCell;
}>;

export const performedDateFormValidator = (datetime: DateTime): Maybe<ValidationErrors> => {
  if (isNil(datetime)) {
    return;
  }

  if (!isDateTimeValid(datetime)) {
    if (isDateInvalid(datetime.date)) {
      return { date: validationError('msg_field_invalid_format') };
    } else if (isDateInvalid(datetime.time)) {
      return { time: validationError('msg_field_invalid_format') };
    } else {
      return;
    }
  }

  return dateTimeToDate(datetime) < currentDate() ?
    { datetime: validationError('msg_campaign_past_perform_date') } : void 0;
};

const EMAIL_CAMPAIGN_MASTER_INFO_ENTITY_CONFIG: EntityConfig<EmailCampaignMasterInfo> = {
  name: { required: true },
  performedDate: {
    required: true,
    validator: (_1, _2, datetime) => performedDateFormValidator(datetime),
  },
};

export const validateEmailCampaignMasterInfo = entityValidatorByEntityConfig(EMAIL_CAMPAIGN_MASTER_INFO_ENTITY_CONFIG);

const EMAIL_CAMPAIGN_DETAILS_INIT_FILTER: EmailCampaignRecipientFilter = {
  masterZones: [],
  offices: [],
  salespersons: [],
  communities: [],
  priorities: [],
  salesFolderStages: [],
  salesFolderStatus: ALL_OPTION,
  typeOfCustomer: ALL_OPTION,
};

const roundDateToMinutes = (date: Date, minutes: number): Date => {
  const cloned = cloneDate(date);
  cloned.setMinutes(Math.ceil(cloned.getMinutes() / minutes) * minutes);
  return cloned;
};

export const emailCampaignDetailsInit = (): EmailCampaignDetails => ({
  campaignId: EMPTY_ID,
  status: EmailCampaignStatus.Created,
  externalId: EMPTY_ID,
  externalListId: EMPTY_ID,
  masterInfo: formStateSnapshot({
    name: '',
    externalTemplate: <any>void 0,
    performedDate: dateTimeFromDate(truncateDateToMinutes(roundDateToMinutes(currentDate(), 15))),
  }),
  appliedFilter: formStateSnapshot(EMAIL_CAMPAIGN_DETAILS_INIT_FILTER),
  currentFilter: formStateSnapshot(EMAIL_CAMPAIGN_DETAILS_INIT_FILTER),
  recipients: [],
  recipientsLoading: false,
  paragraph: '',
});

export const emailCampaignDetailsFromDto = (dto: EmailCampaignDetailsDto): EmailCampaignDetails => {
  const filter = {
    masterZones: dto.masterZones || [],
    offices: dto.offices || [],
    salespersons: dto.salespersons || [],
    communities: dto.communities || [],
    priorities: dto.priorities || [],
    salesFolderStages: dto.salesFolderStages || [],
    typeOfCustomer: dto.typeOfCustomer || ALL_OPTION,
    salesFolderStatus: dto.salesFolderStatus || ALL_OPTION,
  };

  return {
    campaignId: dto.campaignId,
    recipients: dto.recipients,
    recipientsLoading: false,
    paragraph: dto.paragraph,
    status: dto.status,
    externalId: dto.externalId,
    externalListId: dto.externalListId,
    masterInfo: formStateSnapshot({
      name: dto.name,
      externalTemplate: { id: dto.externalTemplateId, name: dto.externalTemplateName },
      performedDate: dateTimeFromDate(dto.performedDate),
    }),
    appliedFilter: formStateSnapshot(filter),
    currentFilter: formStateSnapshot(filter),
    version: dto.version,
  };
};

export const emailCampaignDetailsToDto = (
  details: EmailCampaignDetails,
  { pricing_plan_type }: EmailCampaignAccountInfo): EmailCampaignDetailsDto => {
    const masterInfo = getFormStateSnapshotValue(details.masterInfo);
    const filter = getFormStateSnapshotValue(details.appliedFilter);
    const date = pricing_plan_type !== Enum.toValue(EmailCampaignAccountType, EmailCampaignAccountType.ForeverFree)
      ? dateTimeToDate(masterInfo.performedDate)
      : null;

    return {
// @ts-expect-error Legacy typing suppression
      campaignId: details.campaignId,
// @ts-expect-error Legacy typing suppression
      status: details.status,
// @ts-expect-error Legacy typing suppression
      externalId: details.externalId,
// @ts-expect-error Legacy typing suppression
      externalListId: details.externalListId,
// @ts-expect-error Legacy typing suppression
      paragraph: details.paragraph,
// @ts-expect-error Legacy typing suppression
      externalTemplateId: masterInfo.externalTemplate ? masterInfo.externalTemplate.id : '',
// @ts-expect-error Legacy typing suppression
      externalTemplateName: masterInfo.externalTemplate ? masterInfo.externalTemplate.name : '',
// @ts-expect-error Legacy typing suppression
      name: masterInfo.name,
// @ts-expect-error Legacy typing suppression
      performedDate: date,
      ...cast<EmailCampaignDetailsDto>(omit(filter, ['salesFolderStatus', 'typeOfCustomer'])),
      salesFolderStatus: withoutAllOption<SalesFolderStatus>(filter.salesFolderStatus),
      typeOfCustomer: withoutAllOption<ContactCustomerType>(filter.typeOfCustomer),
      recipients: details.recipients,
      version: details.version,
    };
};

export const emailCampaignDetailsToFilterDto = (details: EmailCampaignDetails): EmailCampaignRecipientFilterDto => {
  const filter = getFormStateSnapshotValue(details.currentFilter);

  return {
    officeIds: map(filter.offices, (office) => office.id),
    masterZoneIds: map(filter.masterZones, (masterZone) => masterZone.id),
    salesPersonIds: map(filter.salespersons, (salesPerson) => salesPerson.id),
    communityIds: map(filter.communities, (community) => community.id),
    priorities: filter.priorities,
    salesFolderStages: filter.salesFolderStages,
    customerType: withoutAllOption<ContactCustomerType>(filter.typeOfCustomer),
    salesFolderStatus: withoutAllOption<SalesFolderStatus>(filter.salesFolderStatus),
  };
};
